import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  FileDown, FlaskConical, Sparkles, Target, BarChart3, BookText,
} from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

import { api } from "../lib/kostra";
import { useAuth } from "../lib/auth";

export default function SampleReportsPage() {
  const { isAdmin, openLogin } = useAuth();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/rnd/sample-reports");
        const list = data?.items || [];
        setItems(list);
        if (list.length > 0) setActive(list[0]);
      } catch {
        toast.error("Could not load sample reports");
      }
    })();
  }, [isAdmin]);

  const pick = (slug) => {
    const m = items.find((i) => i.slug === slug);
    if (m) setActive(m);
  };

  const downloadPdf = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/rnd/projects/${active.id}/export/pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `KOSTRA_Sample_${active.slug.replace(/-/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Sample report downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally { setBusy(false); }
  };

  const downloadExcel = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/rnd/projects/${active.id}/export/excel`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `KOSTRA_Sample_${active.slug.replace(/-/g, "_")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Excel download failed");
    } finally { setBusy(false); }
  };

  // ---- Guest view ---------------------------------------------------------
  if (!isAdmin) {
    return (
      <div className="app-shell">
        <Header />
        <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 text-center">
            <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2">
              <BookText size={14} /> Sample R&amp;D Reports
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-medium mt-3 leading-tight">
              See how KOSTRA optimizes <br className="hidden md:block" />
              <span className="text-[#D4AF37]">a real product launch.</span>
            </h1>
            <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              Browse two pre-built sample R&amp;D reports — Protein Nachos and Electrolyte Recovery
              Bites — with full DOE, regression analysis and optimization. Admin login required to
              view and download.
            </p>
            <Button
              onClick={() => openLogin()}
              className="mt-6 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-11 px-6"
              data-testid="sample-guest-login-btn"
            >
              Admin login to view samples
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ---- Admin view ---------------------------------------------------------
  return (
    <div className="app-shell">
      <Header />
      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 pb-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2">
                <BookText size={14} /> Sample R&amp;D Reports
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-medium mt-2 leading-tight">
                Two ready-to-show <br className="hidden md:block" />
                <span className="text-[#D4AF37]">product optimization stories.</span>
              </h1>
              <p className="text-white/75 text-sm mt-3 max-w-2xl">
                Each report includes the factor design, every trial run, regression results and the
                optimum formulation — exactly the deliverable a food consultancy hands to a client.
              </p>
            </div>
            <div className="flex flex-col items-stretch md:items-end gap-3 shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-white/70 text-xs uppercase tracking-[0.18em] font-semibold">Pick sample</span>
                <Select value={active?.slug || ""} onValueChange={pick}>
                  <SelectTrigger className="h-10 w-72 bg-white/10 border-white/20 text-white"
                                 data-testid="sample-picker">
                    <SelectValue placeholder="Pick a sample..." />
                  </SelectTrigger>
                  <SelectContent>
                    {items.map((it) => (
                      <SelectItem key={it.slug} value={it.slug}>
                        {it.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={downloadPdf}
                disabled={!active || busy}
                className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold"
                data-testid="sample-pdf-btn"
              >
                <FileDown size={14} className="mr-1.5" /> {busy ? "Preparing…" : "Download Sample PDF"}
              </Button>
              <Button
                onClick={downloadExcel}
                disabled={!active || busy}
                variant="outline"
                className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium"
                data-testid="sample-excel-btn"
              >
                <FileDown size={14} className="mr-1.5" /> Excel
              </Button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 -mt-6 pb-12">
        {!active ? (
          <div className="kostra-card p-10 text-center">
            <FlaskConical size={32} className="mx-auto text-[#0A3B24]" />
            <div className="text-sm font-semibold text-[#111827] mt-2">No sample selected</div>
          </div>
        ) : (
          <motion.div
            key={active.slug}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-5"
          >
            {/* Hero card */}
            <div className="kostra-card p-6 md:p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-3xl">
                  <span className="gold-pill text-[10px] font-semibold px-2.5 py-1 rounded-full">
                    {active.category}
                  </span>
                  <h2 className="font-display text-3xl md:text-4xl text-[#0A3B24] mt-2 leading-tight">
                    {active.name}
                  </h2>
                  <p className="text-[#4B5563] text-sm md:text-base mt-2">{active.headline}</p>
                  <p className="text-[#4B5563] text-xs uppercase tracking-[0.16em] mt-2">{active.tagline}</p>
                </div>
                <Button
                  onClick={downloadPdf}
                  disabled={busy}
                  className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold"
                  data-testid="sample-pdf-btn-hero"
                >
                  <FileDown size={14} className="mr-1.5" /> {busy ? "Preparing…" : "Download PDF"}
                </Button>
              </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="sample-stats">
              <StatCard icon={<FlaskConical size={18} />} label="Factors" value={active.factor_count} />
              <StatCard icon={<BarChart3 size={18} />} label="Trials" value={active.trial_count} />
              <StatCard icon={<Target size={18} />} label="Responses Modelled" value={active.response_count} />
              <StatCard icon={<Sparkles size={18} />} label="Method" value="RSM + Desirability" valueClass="text-base" />
            </div>

            {/* Key findings */}
            <div className="kostra-card p-6">
              <h3 className="kostra-section-title text-lg flex items-center gap-2">
                <Sparkles size={16} className="text-[#D4AF37]" /> Key Findings
              </h3>
              <ol className="space-y-3 mt-4 list-none">
                {(active.summary_points || []).map((pt, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-[#0A3B24] text-white text-xs font-semibold inline-flex items-center justify-center font-mono-num">
                      {i + 1}
                    </span>
                    <span className="text-sm text-[#111827] leading-relaxed">{pt}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* CTA */}
            <div className="kostra-card p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-gradient-to-br from-[#FDF9EE] to-white border-[#D4AF37]/30">
              <div>
                <h3 className="kostra-section-title text-lg">Want a report like this for your product?</h3>
                <p className="text-xs text-[#4B5563] mt-1 max-w-xl">
                  Open the R&amp;D Lab, define your own factors and responses, and KOSTRA will guide
                  you to the optimum formulation in a few hours of pilot batches.
                </p>
              </div>
              <Button
                onClick={() => { window.location.href = "/rnd"; }}
                className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold whitespace-nowrap"
                data-testid="sample-cta-rnd"
              >
                Start an R&amp;D Project
              </Button>
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function StatCard({ icon, label, value, valueClass = "kostra-numeric-xl" }) {
  return (
    <div className="kostra-card p-4">
      <div className="flex items-center gap-2 text-[#4B5563]">
        {icon}
        <span className="kostra-data-label">{label}</span>
      </div>
      <div className={`mt-2 font-display text-[#0A3B24] ${valueClass}`}>{value}</div>
    </div>
  );
}
